import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageBanner } from '../Banners/PageBanner'
import { useRequests } from '../../requests/useRequests'
import AppNavBar from '../Header/AppNavBar'

interface NurseryFormData {
  name: string
  description: string
  location: string
  address: string
  contactPhone: string
  contactEmail: string
  website?: string
  tradeType: string
  openingHours: string[]
  image: string
  images: string[]
  specializations: string[]
}

const initialFormData: NurseryFormData = {
  name: '',
  description: '',
  location: '',
  address: '',
  contactPhone: '',
  contactEmail: '',
  website: '',
  tradeType: 'retail',
  openingHours: ['', '', ''],
  image: '',
  images: ['', '', ''],
  specializations: []
}

const specializationOptions = [
  { value: 'drzewa iglaste', label: 'drzewa iglaste' },
  { value: 'drzewa liściaste', label: 'drzewa liściaste' },
  { value: 'krzewy ozdobne', label: 'krzewy ozdobne' },
  { value: 'byliny', label: 'byliny' },
  { value: 'rośliny doniczkowe', label: 'rośliny doniczkowe' },
  { value: 'róże', label: 'róże' },
  { value: 'rośliny wodne', label: 'rośliny wodne' },
  { value: 'sadzonki leśne', label: 'sadzonki leśne' },
  { value: 'owoce i warzywa', label: 'owoce i warzywa' }
]

const NurseryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requests = useRequests()

  const [formData, setFormData] = useState<NurseryFormData>(initialFormData)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isEdit, setIsEdit] = useState<boolean>(false)

  // Dodanie stanów dla obsługi przesyłania zdjęć
  const [mainImageUploading, setMainImageUploading] = useState(false)
  const [additionalImagesUploading, setAdditionalImagesUploading] = useState(false)
  const [mainImagePreview, setMainImagePreview] = useState('')
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([])

  useEffect(() => {
    if (id && id !== 'new') {
      setIsEdit(true)
      loadNursery(parseInt(id))
    }
  }, [id])

  const loadNursery = async (nurseryId: number) => {
    setLoading(true)
    try {
      const response = await requests.getNursery(nurseryId)
      const nurseryData = response.data

      // Uzupełnij brakujące pola w openingHours i images
      const openingHours = nurseryData.openingHours.length > 0
        ? nurseryData.openingHours
        : ['', '', '']

      const images = nurseryData.images && nurseryData.images.length > 0
        ? nurseryData.images
        : ['', '', '']

      setFormData({
        ...nurseryData,
        openingHours,
        images
      })

      setError(null)
    } catch (err) {
      console.error('Błąd podczas ładowania danych szkółki:', err)
      setError('Nie udało się załadować danych szkółki.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleMultiSelectChange = (name: keyof NurseryFormData, value: string) => {
    setFormData(prev => {
      const currentValues = [...(prev[name] as string[])]
      const valueIndex = currentValues.indexOf(value)

      if (valueIndex === -1) {
        return { ...prev, [name]: [...currentValues, value] }
      } else {
        currentValues.splice(valueIndex, 1)
        return { ...prev, [name]: currentValues }
      }
    })
  }

  const handleOpeningHoursChange = (index: number, value: string) => {
    setFormData(prev => {
      const newHours = [...prev.openingHours]
      newHours[index] = value
      return { ...prev, openingHours: newHours }
    })
  }

  // Funkcja do obsługi przesyłania głównego zdjęcia
  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files == null) || e.target.files.length === 0) return

    try {
      setMainImageUploading(true)
      setError(null)

      const file = e.target.files[0]

      // Utwórz URL podglądu
      const previewUrl = URL.createObjectURL(file)
      setMainImagePreview(previewUrl)

      // Prześlij plik do serwera
      const response = await requests.uploadFile(file, 'nursery')

      // Aktualizuj stan formularza
      setFormData(prev => ({
        ...prev,
        image: response.data.fileKey
      }))

      setMainImageUploading(false)
    } catch (err) {
      console.error('Błąd podczas przesyłania głównego zdjęcia:', err)
      setError('Nie udało się przesłać głównego zdjęcia.')
      setMainImageUploading(false)
    }
  }

  // Funkcja do obsługi przesyłania dodatkowych zdjęć
  const handleAdditionalImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files == null) || e.target.files.length === 0) return

    try {
      setAdditionalImagesUploading(true)
      setError(null)

      const files = Array.from(e.target.files)
      const uploadPromises = []
      const newImageKeys = [...formData.images]
      const newPreviews = [...additionalImagePreviews]

      for (const file of files) {
        // Utwórz URL podglądu
        const previewUrl = URL.createObjectURL(file)
        newPreviews.push(previewUrl)

        // Prześlij plik i zapisz promise
        uploadPromises.push(
          requests.uploadFile(file, 'nursery')
            .then(response => {
              newImageKeys.push(response.data.fileKey)
            })
        )
      }

      // Zaktualizuj podglądy od razu
      setAdditionalImagePreviews(newPreviews)

      // Poczekaj na zakończenie wszystkich przesyłań
      await Promise.all(uploadPromises)

      // Zaktualizuj formData po zakończeniu wszystkich przesyłań
      setFormData(prev => ({
        ...prev,
        images: newImageKeys
      }))

      setAdditionalImagesUploading(false)
    } catch (err) {
      console.error('Błąd podczas przesyłania dodatkowych zdjęć:', err)
      setError('Nie udało się przesłać dodatkowych zdjęć.')
      setAdditionalImagesUploading(false)
    }
  }

  // Zmodyfikuj funkcję handleImagesChange, aby obsługiwać indeksy wykraczające poza zakres
  const handleImagesChange = (index: number, value: string) => {
    setFormData(prev => {
      const newImages = [...prev.images]
      if (index >= newImages.length) {
        // Dodaj puste elementy, jeśli indeks wykracza poza zakres
        while (newImages.length <= index) {
          newImages.push('')
        }
      }
      newImages[index] = value
      return { ...prev, images: newImages }
    })
  }

  // Dodaj funkcję do usuwania dodatkowego zdjęcia
  const handleRemoveAdditionalImage = (index: number) => {
    setFormData(prev => {
      const newImages = [...prev.images]
      newImages.splice(index, 1)
      return { ...prev, images: newImages }
    })

    setAdditionalImagePreviews(prev => {
      const newPreviews = [...prev]
      newPreviews.splice(index, 1)
      return newPreviews
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Filtruj puste godziny otwarcia i obrazy
    const filteredHours = formData.openingHours.filter(hour => hour.trim() !== '')
    const filteredImages = formData.images.filter(img => img.trim() !== '')

    // Przygotuj dane do wysłania
    const nurseryData = {
      ...formData,
      openingHours: filteredHours,
      images: filteredImages.length > 0 ? filteredImages : undefined
    }

    try {
      if (isEdit && id) {
        await requests.updateNursery(parseInt(id), nurseryData)
      } else {
        await requests.createNursery(nurseryData)
      }

      navigate('/nurseries')
    } catch (err) {
      console.error('Błąd podczas zapisywania szkółki:', err)
      setError('Wystąpił błąd podczas zapisywania szkółki.')
    } finally {
      setLoading(false)
    }
  }

  if (loading && isEdit) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText={isEdit ? 'Edycja szkółki' : 'Dodaj nową szkółkę'}
        rightIcons={[]}
      />

      <section className='nursery-form pt-60 pb-60'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-12'>
              {error && (
                <div className='alert alert-danger mb-30' role='alert'>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className='nursery-form'>
                <div className='row'>
                  <div className='col-md-6'>
                    <h4 className='form-section-title mb-20'>Podstawowe informacje</h4>

                    <div className='form-group mb-20'>
                      <label htmlFor='name'>Nazwa szkółki <span className='text-danger'>*</span></label>
                      <input
                        type='text'
                        className='form-control'
                        id='name'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='location'>Lokalizacja <span className='text-danger'>*</span></label>
                      <input
                        type='text'
                        className='form-control'
                        id='location'
                        name='location'
                        value={formData.location}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='address'>Adres <span className='text-danger'>*</span></label>
                      <input
                        type='text'
                        className='form-control'
                        id='address'
                        name='address'
                        value={formData.address}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label>Specjalizacje</label>
                      <div className='checkbox-group'>
                        {specializationOptions.map(option => (
                          <div key={option.value} className='form-check'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              id={`specialization-${option.value}`}
                              checked={formData.specializations.includes(option.value)}
                              onChange={() => handleMultiSelectChange('specializations', option.value)}
                            />
                            <label className='form-check-label' htmlFor={`specialization-${option.value}`}>
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='tradeType'>Typ handlu <span className='text-danger'>*</span></label>
                      <select
                        className='form-control'
                        id='tradeType'
                        name='tradeType'
                        value={formData.tradeType}
                        onChange={handleChange}
                        required
                      >
                        <option value='retail'>Handel detaliczny</option>
                        <option value='wholesale'>Handel hurtowy</option>
                        <option value='both'>Hurtowy i detaliczny</option>
                      </select>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <h4 className='form-section-title mb-20'>Kontakt i godziny otwarcia</h4>

                    <div className='form-group mb-20'>
                      <label htmlFor='contactPhone'>Telefon <span className='text-danger'>*</span></label>
                      <input
                        type='text'
                        className='form-control'
                        id='contactPhone'
                        name='contactPhone'
                        value={formData.contactPhone}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='contactEmail'>Email <span className='text-danger'>*</span></label>
                      <input
                        type='email'
                        className='form-control'
                        id='contactEmail'
                        name='contactEmail'
                        value={formData.contactEmail}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='website'>Strona WWW</label>
                      <input
                        type='text'
                        className='form-control'
                        id='website'
                        name='website'
                        value={formData.website}
                        onChange={handleChange}
                        placeholder='np. www.szkolka.pl'
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label>Godziny otwarcia</label>
                      {formData.openingHours.map((hour, index) => (
                        <input
                          key={index}
                          type='text'
                          className='form-control mb-10'
                          value={hour}
                          onChange={(e) => handleOpeningHoursChange(index, e.target.value)}
                          placeholder='np. Poniedziałek - Piątek: 8:00 - 18:00'
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className='row mt-20'>
                  <div className='col-md-12'>
                    <h4 className='form-section-title mb-20'>Zdjęcia i opis</h4>

                    <div className='form-group mb-20'>
                      <label htmlFor='image'>Główne zdjęcie <span className='text-danger'>*</span></label>

                      {/* Dodaj pole do przesyłania pliku */}
                      <div className='custom-file-upload mb-2'>
                        <input
                          type='file'
                          className='custom-file-input'
                          id='mainImageUpload'
                          accept='image/*'
                          onChange={handleMainImageUpload}
                          disabled={mainImageUploading}
                        />
                        <label className='custom-file-label' htmlFor='mainImageUpload'>
                          {mainImageUploading ? 'Przesyłanie...' : 'Wybierz plik'}
                        </label>
                      </div>

                      {/* Wyświetl podgląd głównego zdjęcia */}
                      {(mainImagePreview || formData.image) && (
                        <div className='main-image-preview mb-2'>
                          <img
                            src={mainImagePreview || formData.image}
                            alt='Podgląd głównego zdjęcia'
                            className='img-thumbnail'
                            style={{ maxHeight: '200px' }}
                          />
                        </div>
                      )}

                      {/* Zachowaj istniejący input dla URL */}
                      <input
                        type='text'
                        className='form-control'
                        id='image'
                        name='image'
                        value={formData.image}
                        onChange={handleChange}
                        placeholder='Lub podaj adres URL zdjęcia'
                      />
                    </div>

                    <div className='form-group mb-20'>
                      <label>Dodatkowe zdjęcia</label>

                      {/* Dodaj pole do przesyłania wielu plików */}
                      <div className='custom-file-upload mb-2'>
                        <input
                          type='file'
                          className='custom-file-input'
                          id='additionalImagesUpload'
                          accept='image/*'
                          multiple
                          onChange={handleAdditionalImagesUpload}
                          disabled={additionalImagesUploading}
                        />
                        <label className='custom-file-label' htmlFor='additionalImagesUpload'>
                          {additionalImagesUploading ? 'Przesyłanie...' : 'Wybierz pliki'}
                        </label>
                      </div>

                      {/* Wyświetl podglądy dodatkowych zdjęć */}
                      {(additionalImagePreviews.length > 0 || formData.images.some(img => img)) && (
                        <div className='additional-images-preview row mb-3'>
                          {formData.images.map((image, index) => (
                            image
                              ? (
                                <div key={index} className='col-md-3 mb-2 position-relative'>
                                  <img
                                    src={additionalImagePreviews[index] || image}
                                    alt={`Dodatkowe zdjęcie ${index + 1}`}
                                    className='img-thumbnail'
                                    style={{ maxHeight: '150px' }}
                                  />
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-danger position-absolute'
                                    style={{ top: '5px', right: '20px' }}
                                    onClick={() => handleRemoveAdditionalImage(index)}
                                  >
                                    <i className='fas fa-times' />
                                  </button>
                                </div>
                                )
                              : null
                          ))}
                        </div>
                      )}

                      {/* Zachowaj inputy dla URL */}
                      <div className='mt-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-secondary'
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            images: [...prev.images, '']
                          }))}
                        >
                          <i className='fas fa-plus' /> Dodaj pole URL
                        </button>

                        {formData.images.map((image, index) => (
                          <div key={index} className='input-group mt-2'>
                            <input
                              type='text'
                              className='form-control'
                              value={image}
                              onChange={(e) => handleImagesChange(index, e.target.value)}
                              placeholder={`URL zdjęcia ${index + 1}`}
                            />
                            <div className='input-group-append'>
                              <button
                                type='button'
                                className='btn btn-outline-danger'
                                onClick={() => handleRemoveAdditionalImage(index)}
                              >
                                <i className='fas fa-times' />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className='form-group mb-20'>
                      <label htmlFor='description'>Opis <span className='text-danger'>*</span></label>
                      <textarea
                        className='form-control'
                        id='description'
                        name='description'
                        rows={5}
                        value={formData.description}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className='form-actions mt-40 text-end'>
                  <button
                    type='button'
                    className={`
                                      tw-mr-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                    onClick={() => navigate('/nurseries')}
                  >
                    Anuluj
                  </button>

                  <button
                    type='submit'
                    className={`
                        tw-inline-flex tw-items-center tw-justify-center
                        tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                        tw-text-white
                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                        tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                        disabled:opacity-75 disabled:cursor-not-allowed
                      `}
                    disabled={loading}
                  >
                    {loading
                      ? (
                        <>
                          <span
                            className='tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent tw-mr-2'
                            role='status'
                            aria-hidden='true'
                          />
                          {isEdit ? 'Aktualizowanie...' : 'Zapisywanie...'}
                        </>
                        )
                      : (
                          isEdit ? 'Zaktualizuj szkółkę' : 'Dodaj szkółkę'
                        )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default NurseryForm
